<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-container glass-modal-container modal-evidence">
      <div class="modal-header glass-modal-header">
        <h3>Evidence for Dispute #{{ dispute ? dispute.invoice_id : '' }}</h3>
        <button class="modal-close" @click="close">×</button>
      </div>
      <div class="modal-body">
        <div class="evidence-tabs">
          <button 
            :class="['evidence-tab glass-tab', { active: activeTab === 'main' }]"
            @click="activeTab = 'main'"
            v-if="dispute && dispute.image_path"
          >
            <i class="fas fa-image"></i> Main Image
          </button>
          <button 
            :class="['evidence-tab glass-tab', { active: activeTab === 'documents' }]"
            @click="activeTab = 'documents'"
            v-if="dispute && dispute.documents && dispute.documents.length > 0"
          >
            <i class="fas fa-file-alt"></i> Documents ({{ dispute.documents ? dispute.documents.length : 0 }})
          </button>
          <button 
            :class="['evidence-tab glass-tab', { active: activeTab === 'history' }]"
            @click="activeTab = 'history'; fetchLocalOrderHistory()"
          >
            <i class="fas fa-truck"></i> Order History
          </button>
          <button 
            :class="['evidence-tab glass-tab', { active: activeTab === 'dispute_details' }]"
            @click="activeTab = 'dispute_details'"
          >
            <i class="fas fa-info-circle"></i> Dispute Details
          </button>
        </div>

        <!-- Main Image Tab -->
        <div v-if="activeTab === 'main' && dispute && dispute.image_path" class="evidence-content">
          <div class="image-container">
            <img 
              :src="getImagePath(dispute.image_path)" 
              class="evidence-image"
              alt="Main evidence"
              @click="viewFullImage"
            />
            <div class="image-caption">
              <p><strong>Main Evidence Image</strong></p>
              <p>Click image to view full size</p>
            </div>
          </div>
        </div>

        <!-- Documents Tab -->
        <div v-if="activeTab === 'documents' && dispute && dispute.documents && dispute.documents.length > 0" class="evidence-content">
          <div class="documents-grid">
            <div 
              v-for="(doc, index) in dispute.documents" 
              :key="index" 
              class="document-card glass-card-inner"
              @click="viewDocument(doc)"
            >
              <div class="document-icon">
                <i v-if="isPdf(doc.file_path)" class="fas fa-file-pdf"></i>
                <i v-else-if="doc.file_path && doc.file_path.match(/\.(jpg|jpeg|png|gif|webp)$/i)" class="fas fa-file-image"></i>
                <i v-else class="fas fa-file-alt"></i>
              </div>
              <div class="document-info">
                <div class="document-title">{{ doc.title || 'Untitled Document' }}</div>
                <div class="document-type">{{ doc.document_type || 'Document' }}</div>
                <div class="document-date">{{ formatDate(doc.created_at) }}</div>
                <div class="document-notes-preview" v-if="doc.notes">{{ truncateText(doc.notes, 50) }}</div>
              </div>
              <button class="view-doc-btn glass-btn-small">
                <i class="fas fa-eye"></i> View
              </button>
            </div>
          </div>
        </div>

        <!-- Order History Tab - Full Details -->
        <div v-if="activeTab === 'history'" class="evidence-content envio-history-container">
          <div v-if="localHistoryLoading" class="history-loading">
            <div class="spinner"></div>
            <p>Fetching order history from Envio...</p>
          </div>
          
          <div v-else-if="localOrderHistory" class="order-history-wrapper">
            <!-- Order Summary Header -->
            <div class="order-summary-header-enhanced">
              <div class="order-info-group">
                <div class="order-badge-enhanced">
                  <i class="fas fa-truck-fast"></i>
                  Order #{{ getOrderNumber() }}
                </div>
                <!-- <div class="order-meta">
                  <span class="meta-item">
                    <i class="fas fa-calendar-alt"></i>
                    {{ formatDateTime(getOrderCreatedAt()) }}
                  </span>
                  <span class="meta-item" v-if="getOrderDeliveryNo()">
                    <i class="fas fa-hashtag"></i>
                    Delivery: {{ getOrderDeliveryNo() }}
                  </span>
                </div> -->
              </div>
              <div class="order-status-enhanced" :class="getOrderStatusClass()">
                <i :class="getStatusIcon()"></i>
                {{ getOrderStatus() }}
              </div>
            </div>

            <!-- Quick Stats Cards -->
            <div class="quick-stats-grid">
              <div class="stat-card-enhanced">
                <div class="stat-icon">💰</div>
                <div class="stat-details">
                  <span class="stat-label-enhanced">Total Amount</span>
                  <strong class="stat-value-enhanced">${{ getOrderTotalAmount() }}</strong>
                </div>
              </div>
              <div class="stat-card-enhanced">
                <div class="stat-icon">📦</div>
                <div class="stat-details">
                  <span class="stat-label-enhanced">Items</span>
                  <strong class="stat-value-enhanced">{{ getItemsCount() }}</strong>
                </div>
              </div>
              <div class="stat-card-enhanced">
                <div class="stat-icon">🚚</div>
                <div class="stat-details">
                  <span class="stat-label-enhanced">Delivery Charges</span>
                  <strong class="stat-value-enhanced">${{ getDeliveryCharges() }}</strong>
                </div>
              </div>
              <div class="stat-card-enhanced" v-if="getServiceTypeName()">
                <div class="stat-icon">🏷️</div>
                <div class="stat-details">
                  <span class="stat-label-enhanced">Service Type</span>
                  <strong class="stat-value-enhanced">{{ getServiceTypeName() }}</strong>
                </div>
              </div>
            </div>

            <!-- All Sections -->
            <div class="envio-sections">
              <!-- Customer Section -->
              <div class="envio-section">
                <div class="section-header" @click="toggleSection('customer')">
                  <i class="fas fa-user-circle"></i>
                  <h4>Customer Information</h4>
                  <i :class="isSectionOpen('customer') ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="section-toggle"></i>
                </div>
                <div v-show="openSections.customer" class="section-content">
                  <div class="customer-info-grid">
                    <div class="info-chip full-width"><i class="fas fa-user"></i><span><strong>Name:</strong> {{ getCustomerNameFromOrder() }}</span></div>
                    <div class="info-chip"><i class="fas fa-phone"></i><span><strong>Phone:</strong> {{ getCustomerPhoneFromOrder() }}</span></div>
                    <div class="info-chip"><i class="fas fa-envelope"></i><span><strong>Email:</strong> {{ getCustomerEmailFromOrder() }}</span></div>
                    <div class="info-chip full-width"><i class="fas fa-map-marker-alt"></i><span><strong>Address:</strong> {{ getCustomerAddress() }}</span></div>
                  </div>
                </div>
              </div>

              <!-- Items Section with Full Details -->
              <div class="envio-section" v-if="getItems() && getItems().length">
                <div class="section-header" @click="toggleSection('items')">
                  <i class="fas fa-boxes"></i>
                  <h4>Order Items <span class="item-count">{{ getItems().length }}</span></h4>
                  <i :class="isSectionOpen('items') ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="section-toggle"></i>
                </div>
                <div v-show="openSections.items" class="section-content">
                  <div class="items-grid-enhanced">
                    <div v-for="(item, idx) in getItems()" :key="idx" class="item-card-enhanced">
                      <div class="item-header">
                        <div class="item-name-enhanced">
                          <strong>{{ item.item_name }}</strong>
                          <span class="item-sku-enhanced" v-if="item.item_sku">SKU: {{ item.item_sku }}</span>
                        </div>
                        <span class="status-badge-enhanced" :class="getItemStatusClass(item.status)">
                          <i :class="getItemStatusIcon(item.status)"></i>
                          {{ item.status || 'Pending' }}
                        </span>
                      </div>
                      <div class="item-details">
                        <div class="detail-row">
                          <span class="detail-label">Quantity:</span>
                          <span class="detail-value">{{ item.item_quantity }}</span>
                        </div>
                        <div class="detail-row" v-if="item.price">
                          <span class="detail-label">Price:</span>
                          <span class="detail-value">${{ parseFloat(item.price).toFixed(2) }}</span>
                        </div>
                        <div class="detail-row" v-if="item.location">
                          <span class="detail-label">Location:</span>
                          <span class="detail-value">{{ item.location }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Timeline Section with Full Details -->
              <div class="envio-section" v-if="getHistory() && getHistory().length">
                <div class="section-header" @click="toggleSection('timeline')">
                  <i class="fas fa-history"></i>
                  <h4>Activity Timeline ({{ getHistory().length }} events)</h4>
                  <i :class="isSectionOpen('timeline') ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="section-toggle"></i>
                </div>
                <div v-show="openSections.timeline" class="section-content">
                  <div class="timeline-enhanced">
                    <div v-for="(event, idx) in getHistory()" :key="idx" class="timeline-item-enhanced">
                      <div class="timeline-marker" :class="getTimelineMarkerClass(event)">
                        <i :class="getTimelineIcon(event.description)"></i>
                      </div>
                      <div class="timeline-card">
                        <div class="timeline-header">
                          <span class="timeline-date-enhanced">{{ formatDateTime(event.created_at) }}</span>
                          <span class="timeline-type" v-if="event.operator_name">
                            <i class="fas fa-user-check"></i> {{ event.operator_name }}
                          </span>
                        </div>
                        <div class="timeline-description-enhanced">
                          {{ event.complete_desc || event.description }}
                        </div>
                        <div class="timeline-location-enhanced" v-if="event.latitude && event.latitude != 0">
                          <i class="fas fa-location-dot"></i>
                          Location: {{ event.latitude }}, {{ event.longitude }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Delivery Info Section -->
              <div class="envio-section" v-if="hasDeliveryInfo">
                <div class="section-header" @click="toggleSection('delivery')">
                  <i class="fas fa-truck"></i>
                  <h4>Delivery Information</h4>
                  <i :class="isSectionOpen('delivery') ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="section-toggle"></i>
                </div>
                <div v-show="openSections.delivery" class="section-content">
                  <div class="delivery-info-grid">
                    <div class="info-group">
                      <label><i class="fas fa-truck"></i> Truck</label>
                      <p><strong>{{ getTruckName() }}</strong> <span class="text-muted">({{ getTruckLicense() }})</span></p>
                    </div>
                    <div class="info-group">
                      <label><i class="fas fa-id-card"></i> Driver</label>
                      <p>{{ getDispatcherName() }}</p>
                    </div>
                    <div class="info-group">
                      <label><i class="fas fa-barcode"></i> Trip #</label>
                      <p>{{ getTripNumber() }}</p>
                    </div>
                    <div class="info-group">
                      <label><i class="fas fa-building"></i> Contractor</label>
                      <p>{{ getContractorName() }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Route Section -->
              <div class="envio-section" v-if="hasRouteInfo">
                <div class="section-header" @click="toggleSection('route')">
                  <i class="fas fa-route"></i>
                  <h4>Route Information</h4>
                  <i :class="isSectionOpen('route') ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="section-toggle"></i>
                </div>
                <div v-show="openSections.route" class="section-content">
                  <div class="route-info">
                    <div class="route-stats">
                      <div class="route-stat">
                        <span class="stat-label">Distance</span>
                        <span class="stat-number">{{ getRouteDistance() }}</span>
                      </div>
                      <div class="route-stat">
                        <span class="stat-label">Travel Time</span>
                        <span class="stat-number">{{ getRouteTravelTime() }} min</span>
                      </div>
                      <div class="route-stat">
                        <span class="stat-label">Mileage</span>
                        <span class="stat-number">{{ getRouteMileage() }}</span>
                      </div>
                    </div>
                    <div class="schedule-info">
                      <div class="schedule-item">
                        <i class="far fa-calendar-alt"></i>
                        <div>
                          <small>Scheduled</small>
                          <p>{{ getRouteScheduleDate() }}<br>{{ getRouteEstStartTime() }} - {{ getRouteEstEndTime() }}</p>
                        </div>
                      </div>
                      <div class="schedule-item" v-if="getRouteActStartTime()">
                        <i class="fas fa-play-circle"></i>
                        <div>
                          <small>Actual Start</small>
                          <p>{{ formatDateTime(getRouteActStartTime()) }}</p>
                        </div>
                      </div>
                      <div class="schedule-item" v-if="getRouteActEndTime()">
                        <i class="fas fa-flag-checkered"></i>
                        <div>
                          <small>Actual End</small>
                          <p>{{ formatDateTime(getRouteActEndTime()) }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Delivery Proof Section with Images -->
              <div class="envio-section" v-if="hasProof">
                <div class="section-header" @click="toggleSection('proof')">
                  <i class="fas fa-camera"></i>
                  <h4>Delivery Proof</h4>
                  <i :class="isSectionOpen('proof') ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="section-toggle"></i>
                </div>
                <div v-show="openSections.proof" class="section-content">
                  <div class="proof-gallery">
                    <div v-if="getReceiverSignature()" class="signature-card">
                      <label><i class="fas fa-signature"></i> Signature</label>
                      <img :src="getReceiverSignaturePath()" class="signature-enhanced" alt="Signature" />
                    </div>
                    <div v-if="getImages() && getImages().length" class="images-gallery">
                      <label><i class="fas fa-images"></i> Delivery Images ({{ getImages().length }})</label>
                      <div class="image-grid-enhanced">
                        <div 
                          v-for="(img, idx) in getImages()" 
                          :key="idx"
                          class="image-card"
                          @click="viewImageUrl(img.image_path)"
                        >
                          <img :src="img.image_path" :alt="'Delivery image ' + (idx + 1)" />
                          <div class="image-overlay">
                            <i class="fas fa-search-plus"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="no-history">
            <i class="fas fa-history"></i>
            <p>No order history available from Envio.</p>
            <button @click="fetchLocalOrderHistory" class="btn-primary glass-btn-primary">
              <i class="fas fa-sync-alt"></i> Fetch Order History
            </button>
          </div>
        </div>

        <!-- Dispute Details Tab -->
        <div v-if="activeTab === 'dispute_details'" class="evidence-content">
          <div class="dispute-details-card glass-card-inner">
            <h4><i class="fas fa-info-circle"></i> Dispute Information</h4>
            <div class="details-grid">
              <div class="detail-item">
                <label>Dispute ID</label>
                <div class="detail-value">{{ dispute.id }}</div>
              </div>
              <div class="detail-item">
                <label>Invoice #</label>
                <div class="detail-value">{{ dispute.invoice_id }}</div>
              </div>
              <div class="detail-item">
                <label>Status</label>
                <div class="detail-value">
                  <span :class="dispute.status === 'resolved' ? 'status-resolved' : 'status-open'" class="status-badge">
                    {{ dispute.status === 'resolved' ? 'Resolved' : 'Open' }}
                  </span>
                </div>
              </div>
              <div class="detail-item">
                <label>Dispute Type</label>
                <div class="detail-value">{{ formatDisputeType(dispute.dispute_type) }}</div>
              </div>
              <div class="detail-item">
                <label>Finance Company</label>
                <div class="detail-value">{{ dispute.dispute_company_name || dispute.finance_company_name || '-' }}</div>
              </div>
              <div class="detail-item">
                <label>Customer Statement</label>
                <div class="detail-value">{{ dispute.customer_statement === 'received' ? 'Received' : 'Not Received' }}</div>
              </div>
              <div class="detail-item">
                <label>Issue Type</label>
                <div class="detail-value">{{ formatIssueType(dispute.issue_type) }}</div>
              </div>
              <div class="detail-item">
                <label>Resolution</label>
                <div class="detail-value">{{ formatResolution(dispute.resolution) }}</div>
              </div>
              <div class="detail-item">
                <label>Money Lost</label>
                <div class="detail-value">{{ dispute.money_lost ? 'Yes' : 'No' }}</div>
              </div>
              <div class="detail-item">
                <label>Created Date</label>
                <div class="detail-value">{{ formatDate(dispute.created_at) }}</div>
              </div>
              <div class="detail-item" v-if="dispute.resolved_at">
                <label>Resolved Date</label>
                <div class="detail-value">{{ formatDate(dispute.resolved_at) }}</div>
              </div>
              <div class="detail-item full-width">
                <label>Dispute Details</label>
                <div class="detail-value">{{ dispute.details || 'No additional details provided.' }}</div>
              </div>
              <div class="detail-item full-width" v-if="dispute.resolution_notes">
                <label>Resolution Notes</label>
                <div class="detail-value">{{ dispute.resolution_notes }}</div>
              </div>
            </div>
          </div>
        </div>

        <!-- No Evidence Message -->
        <div v-if="activeTab !== 'history' && activeTab !== 'dispute_details' && (!dispute || !dispute.image_path) && (!dispute || !dispute.documents || dispute.documents.length === 0)" class="no-evidence">
          <i class="fas fa-folder-open"></i>
          <p>No evidence available for this dispute.</p>
          <button @click="addDocument" class="btn-primary glass-btn-primary">
            <i class="fas fa-plus"></i> Add Document
          </button>
        </div>
      </div>
      <div class="modal-footer glass-modal-footer">
        <button class="btn-secondary glass-btn" @click="close">
          <i class="fas fa-times"></i> Close
        </button>
        <button v-if="activeTab !== 'history'" class="btn-primary glass-btn-primary" @click="addDocument">
          <i class="fas fa-plus"></i> Add More Evidence
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import Requestor from "@/utilities/Requestor"

export default {
  name: 'EvidenceModal',
  props: {
    visible: { type: Boolean, default: false },
    dispute: { type: Object, default: null },
    orderHistory: { type: Object, default: null },
    historyLoading: { type: Boolean, default: false }
  },
  data() {
    return {
      activeTab: 'main',
      openSections: {
        customer: true,
        items: true,
        timeline: true,
        delivery: false,
        route: false,
        proof: false
      },
      localOrderHistory: null,
      localHistoryLoading: false
    }
  },
  computed: {
    hasDeliveryInfo() {
      const order = this.getOrder()
      return !!(order && (order.truck || order.dispatcher))
    },
    hasRouteInfo() {
      return !!this.getRoute()
    },
    hasProof() {
      const order = this.getOrder()
      return !!(order && (order.receiver_signature || (order.images && order.images.length > 0)))
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.activeTab = 'main'
        this.localOrderHistory = null
      }
    },
    orderHistory: {
      handler(val) {
        if (val) {
          this.localOrderHistory = val
        }
      },
      deep: true
    }
  },
  methods: {
    // FIXED: Enhanced getImagePath to handle all types of paths
    getImagePath(path) {
      if (!path) return '';
      
      // If it's already a full URL, return as is
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // Remove leading slash to avoid double slashes
      let cleanPath = path.startsWith('/') ? path.substring(1) : path;
      
      // Get base URL from environment or use current origin
      const baseUrl = process.env.VUE_APP_API_URL || window.location.origin;
      
      // Ensure proper formatting
      return `${baseUrl}/${cleanPath}`;
    },
    
    // FIXED: Enhanced isPdf to handle various PDF patterns
    isPdf(url) {
      if (!url) return false;
      const lowerUrl = url.toLowerCase();
      return lowerUrl.endsWith('.pdf') || lowerUrl.includes('.pdf?') || lowerUrl.includes('.pdf#');
    },
    
    // FIXED: Enhanced viewFullImage with proper URL handling
    viewFullImage() {
      if (this.dispute && this.dispute.image_path) {
        const fullImageUrl = this.getImagePath(this.dispute.image_path);
        console.log('Opening full image:', fullImageUrl); // Debug log
        this.$emit('view-image', fullImageUrl);
      } else {
        console.warn('No image path available for dispute:', this.dispute);
      }
    },
    
    // FIXED: Enhanced viewImageUrl with proper URL handling
    viewImageUrl(url) {
      if (url) {
        const fullUrl = this.getImagePath(url);
        console.log('Opening delivery image:', fullUrl); // Debug log
        this.$emit('view-image', fullUrl);
      } else {
        console.warn('No image URL provided');
      }
    },
    
    // FIXED: Enhanced viewDocument with proper handling for different file types
    viewDocument(doc) {
      if (doc && doc.file_path) {
        const fileUrl = this.getImagePath(doc.file_path);
        
        // For PDFs, open in new tab directly
        if (this.isPdf(doc.file_path)) {
          console.log('Opening PDF in new tab:', fileUrl);
          window.open(fileUrl, '_blank');
        } 
        // For images, use the image modal
        else if (doc.file_path && doc.file_path.match(/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i)) {
          console.log('Opening document image:', fileUrl);
          this.$emit('view-image', fileUrl);
        }
        // For other documents, emit document event
        else {
          console.log('Opening other document:', fileUrl);
          this.$emit('view-document', { ...doc, file_url: fileUrl });
        }
      } else {
        console.warn('No file path for document:', doc);
      }
    },
    
    // Helper method to verify if an image exists (optional, for debugging)
    async verifyImageUrl(url) {
      try {
        const response = await fetch(url, { method: 'HEAD' });
        if (!response.ok) {
          console.warn(`Image not accessible: ${url} (Status: ${response.status})`);
          return false;
        }
        console.log(`Image accessible: ${url}`);
        return true;
      } catch (error) {
        console.error(`Error checking image: ${url}`, error);
        return false;
      }
    },
    
    // Add a mounted hook for debugging (optional)
    async mounted() {
      if (this.dispute && this.dispute.image_path) {
        const imageUrl = this.getImagePath(this.dispute.image_path);
        console.log('Main image URL constructed:', imageUrl);
        await this.verifyImageUrl(imageUrl);
      }
    },
    
    // Rest of your existing methods remain exactly the same
    formatDate(date) {
      if (!date) return 'N/A'
      const d = new Date(date)
      return d.toLocaleDateString()
    },
    
    formatDateTime(dateStr) {
      if (!dateStr) return 'N/A'
      const date = new Date(dateStr)
      return date.toLocaleString()
    },
    
    truncateText(text, length) {
      if (!text) return ''
      return text.length > length ? text.substring(0, length) + '...' : text
    },
    
    toggleSection(section) {
      this.$set(this.openSections, section, !this.openSections[section])
    },
    
    isSectionOpen(section) {
      return this.openSections[section] !== false
    },
    
    formatDisputeType(type) {
      const types = {
        'customer': 'Customer',
        'credit_card': 'Credit Card',
        'snap_finance': 'Finance',
        'merchant_service': 'Merchant Service',
        'attorney': 'Attorney',
        'court': 'Court'
      }
      return types[type] || type
    },
    
    formatIssueType(type) {
      const types = {
        'fraud_charges': 'Fraud Charges',
        'broken_merchandise': 'Broken Merchandise',
        'wrong_item': 'Wrong Item',
        'missing_item': 'Missing Item',
        'not_as_described': 'Not as Described',
        'delivery_issue': 'Delivery Issue',
        'other': 'Other'
      }
      return types[type] || type
    },
    
    formatResolution(resolution) {
      const resolutions = {
        'customer_favor': "Customer's Favor",
        'business_favor': "Business's Favor",
        'partial_refund': 'Partial Refund',
        'full_refund': 'Full Refund',
        'replacement': 'Replacement',
        'chargeback_won': 'Chargeback Won',
        'chargeback_lost': 'Chargeback Lost',
        'legal_action': 'Legal Action',
        'pending': 'Pending Resolution'
      }
      return resolutions[resolution] || resolution
    },

    async fetchLocalOrderHistory() {
      if (!this.dispute || !this.dispute.invoice_id) {
        console.error('No dispute or invoice ID available')
        return
      }
      
      this.localHistoryLoading = true
      
      try {
        const response = await Requestor.getAsync(
          `/api/sales/envio_get_order_with_history?order_no=${this.dispute.invoice_id}`
        )
        
        if (response.data && response.data.status === true) {
          this.localOrderHistory = response.data.result
          this.$emit('update:orderHistory', this.localOrderHistory)
        } else {
          console.error(response.data.message || "Failed to fetch order history")
          this.localOrderHistory = null
        }
      } catch (error) {
        console.error('Error fetching order history:', error)
        this.localOrderHistory = null
      } finally {
        this.localHistoryLoading = false
      }
    },

    getOrder() {
      return this.localOrderHistory && this.localOrderHistory.order ? this.localOrderHistory.order : null
    },
    
    getOrderNumber() {
      const order = this.getOrder()
      return order && order.order_no ? order.order_no : (this.dispute ? this.dispute.invoice_id : 'N/A')
    },
    
    getOrderCreatedAt() {
      const order = this.getOrder()
      return order ? order.created_at : null
    },
    
    getOrderDeliveryNo() {
      const order = this.getOrder()
      return order ? order.delivery_no : null
    },
    
    getOrderStatus() {
      const order = this.getOrder()
      return order && order.status ? order.status : 'Unknown'
    },
    
    getOrderStatusClass() {
      const status = this.getOrderStatus()
      const classes = { 'Finished': 'status-finished', 'Started': 'status-started', 'In Transit': 'status-transit', 'Scheduled': 'status-scheduled' }
      return classes[status] || 'status-unknown'
    },
    
    getStatusIcon() {
      const status = this.getOrderStatus()
      const icons = { 'Finished': 'fas fa-check-circle', 'Started': 'fas fa-play-circle', 'In Transit': 'fas fa-truck-moving', 'Scheduled': 'fas fa-calendar-check' }
      return icons[status] || 'fas fa-question-circle'
    },
    
    getOrderTotalAmount() {
      const order = this.getOrder()
      const amount = order ? order.total_amount : 0
      return parseFloat(amount || 0).toFixed(2)
    },
    
    getItemsCount() {
      const items = this.getItems()
      return items ? items.length : 0
    },
    
    getDeliveryCharges() {
      const order = this.getOrder()
      const charges = order ? order.contractor_delivery_charges : 0
      return parseFloat(charges || 0).toFixed(2)
    },
    
    getServiceTypeName() {
      const order = this.getOrder()
      const serviceType = order ? order.serviceType : null
      return serviceType && serviceType.type ? serviceType.type : null
    },
    
    getCustomerNameFromOrder() {
      const order = this.getOrder()
      const customer = order && order.Customer ? order.Customer : null
      return customer && customer.name ? customer.name : 'N/A'
    },
    
    getCustomerPhoneFromOrder() {
      const order = this.getOrder()
      const customer = order && order.Customer ? order.Customer : null
      return customer && customer.phone1 ? customer.phone1 : 'N/A'
    },
    
    getCustomerEmailFromOrder() {
      const order = this.getOrder()
      const customer = order && order.Customer ? order.Customer : null
      return customer && customer.email ? customer.email : 'N/A'
    },
    
    getCustomerAddress() {
      const order = this.getOrder()
      const customer = order && order.Customer ? order.Customer : null
      if (!customer) return 'N/A'
      const parts = [customer.address1, customer.city, customer.state, customer.zipcode].filter(p => p)
      return parts.join(', ') || 'N/A'
    },
    
    getItems() {
      const order = this.getOrder()
      return order && order.Items ? order.Items : []
    },
    
    getHistory() {
      return this.localOrderHistory && this.localOrderHistory.history ? this.localOrderHistory.history : []
    },
    
    getTruckName() {
      const order = this.getOrder()
      const truck = order && order.truck ? order.truck : null
      return truck && truck.truck_name ? truck.truck_name : 'N/A'
    },
    
    getTruckLicense() {
      const order = this.getOrder()
      const truck = order && order.truck ? order.truck : null
      return truck && truck.license_no ? truck.license_no : 'N/A'
    },
    
    getDispatcherName() {
      const order = this.getOrder()
      const dispatcher = order && order.dispatcher ? order.dispatcher : null
      return dispatcher && dispatcher.name ? dispatcher.name : 'N/A'
    },
    
    getTripNumber() {
      const order = this.getOrder()
      const trip = order && order.trip ? order.trip : null
      return trip && trip.trip_no ? trip.trip_no : 'N/A'
    },
    
    getContractorName() {
      const order = this.getOrder()
      const dispatcher = order && order.dispatcher ? order.dispatcher : null
      const contractor = dispatcher && dispatcher.contractor ? dispatcher.contractor : null
      return contractor && contractor.name ? contractor.name : 'N/A'
    },
    
    getRoute() {
      const order = this.getOrder()
      return order && order.Route ? order.Route : null
    },
    
    getRouteDistance() {
      const route = this.getRoute()
      return route ? (route.distance || 'N/A') : 'N/A'
    },
    
    getRouteTravelTime() {
      const route = this.getRoute()
      return route ? (route.travel_time || 'N/A') : 'N/A'
    },
    
    getRouteMileage() {
      const route = this.getRoute()
      return route ? (route.act_mileage || 'N/A') : 'N/A'
    },
    
    getRouteScheduleDate() {
      const route = this.getRoute()
      return route ? this.formatDate(route.schedule_date) : 'N/A'
    },
    
    getRouteEstStartTime() {
      const route = this.getRoute()
      return route ? (route.est_start_time || 'N/A') : 'N/A'
    },
    
    getRouteEstEndTime() {
      const route = this.getRoute()
      return route ? (route.est_end_time || 'N/A') : 'N/A'
    },
    
    getRouteActStartTime() {
      const route = this.getRoute()
      return route ? route.act_start_time : null
    },
    
    getRouteActEndTime() {
      const route = this.getRoute()
      return route ? route.act_end_time : null
    },
    
    getReceiverSignature() {
      const order = this.getOrder()
      return order && order.receiver_signature ? order.receiver_signature : null
    },
    
    getReceiverSignaturePath() {
      const order = this.getOrder()
      const signature = order && order.receiver_signature_path ? order.receiver_signature_path : null
      if (signature && signature.startsWith('http')) {
        return signature
      }
      return signature ? this.getImagePath(signature) : ''
    },
    
    getImages() {
      const order = this.getOrder()
      const images = order && order.images ? order.images : []
      // Ensure all images have proper paths
      return images.map(img => ({
        ...img,
        image_path: this.getImagePath(img.image_path)
      }))
    },
    
    getItemStatusClass(status) {
      const classes = {
        'delivered': 'status-delivered',
        'pending': 'status-pending',
        'cancelled': 'status-cancelled'
      }
      return classes[status] || 'status-unknown'
    },
    
    getItemStatusIcon(status) {
      const icons = {
        'delivered': 'fas fa-check',
        'pending': 'fas fa-clock',
        'cancelled': 'fas fa-times'
      }
      return icons[status] || 'fas fa-question'
    },
    
    getTimelineMarkerClass(event) {
      const desc = (event.description || '').toLowerCase()
      if (desc.includes('status changed')) return 'timeline-status-marker'
      if (desc.includes('image uploaded') || desc.includes('signature')) return 'timeline-upload-marker'
      if (desc.includes('text message') || desc.includes('sent to')) return 'timeline-message-marker'
      return 'timeline-default-marker'
    },
    
    getTimelineIcon(description) {
      const desc = (description || '').toLowerCase()
      if (desc.includes('status changed')) return 'fas fa-exchange-alt'
      if (desc.includes('image uploaded')) return 'fas fa-camera'
      if (desc.includes('signature')) return 'fas fa-signature'
      if (desc.includes('text message')) return 'fas fa-sms'
      return 'fas fa-info-circle'
    },

    addDocument() {
      this.$emit('add-document', this.dispute)
    },
    
    refreshHistory() {
      this.fetchLocalOrderHistory()
      this.$emit('refresh-history')
    },
    
    close() {
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
/* Add any missing styles here - keeping all existing styles */
.status-badge {
  display: inline-block;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  font-weight: 600;
  text-transform: uppercase;
}
.no-evidence{
    text-align: center;
    align-items: center;
}

.status-open {
  background: rgba(245, 158, 11, 0.1);
  color: #d97706;
}

.status-resolved {
  background: rgba(16, 185, 129, 0.1);
  color: #059669;
}

.status-delivered {
  background: #d1fae5;
  color: #065f46;
}

.status-pending {
  background: #fed7aa;
  color: #9a3412;
}

.status-cancelled {
  background: #fee2e2;
  color: #991b1b;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  margin-bottom: 8px;
}

.detail-item.full-width {
  grid-column: span 2;
}

.detail-item label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  color: #1e293b;
  word-break: break-word;
}

.delivery-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.info-group label {
  font-size: 11px;
  color: #94a3b8;
  display: block;
  margin-bottom: 4px;
}

.info-group p {
  font-size: 14px;
  color: #1e293b;
  margin: 0;
}

.route-stats {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.route-stat {
  text-align: center;
}

.route-stat .stat-label {
  font-size: 11px;
  color: #94a3b8;
  display: block;
}

.route-stat .stat-number {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.schedule-info {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.schedule-item {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.schedule-item i {
  font-size: 20px;
  color: #3b82f6;
  margin-top: 2px;
}

.schedule-item small {
  font-size: 10px;
  color: #94a3b8;
  display: block;
}

.schedule-item p {
  margin: 0;
  font-size: 13px;
  color: #1e293b;
}

.proof-gallery {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.signature-card {
  text-align: center;
}

.signature-card label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  display: block;
  margin-bottom: 8px;
}

.signature-enhanced {
  max-width: 300px;
  max-height: 100px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  background: white;
}

.image-grid-enhanced {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 12px;
}

.image-card {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  aspect-ratio: 1;
}

.image-card img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.2s;
}

.image-card:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-card:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  color: white;
  font-size: 24px;
}

.item-sku-enhanced {
  font-size: 10px;
  color: #94a3b8;
  font-family: monospace;
  display: block;
}

.document-notes-preview {
  font-size: 10px;
  color: #64748b;
  margin-top: 4px;
}

.image-container {
  text-align: center;
}

.image-caption {
  margin-top: 12px;
  color: #64748b;
  font-size: 12px;
}

.dispute-details-card {
  padding: 20px;
}

.dispute-details-card h4 {
  margin-bottom: 16px;
  color: #1e293b;
}

.text-muted {
  color: #94a3b8;
  font-size: 12px;
}

.modal-evidence .modal-container {
  max-width: 1100px;
  width: 90%;
}

/* Keep all existing styles from the original component */
.evidence-tabs {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 12px;
}

.evidence-tab {
  padding: 8px 20px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
  background: transparent;
  border: none;
}

.evidence-tab.active {
  background: #1e293b;
  color: white;
}

.evidence-content {
  min-height: 400px;
}

.evidence-image {
  max-width: 100%;
  max-height: 60vh;
  object-fit: contain;
  border-radius: 8px;
  cursor: pointer;
}

.documents-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
}

.document-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
}

.document-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.document-icon {
  font-size: 32px;
  color: #64748b;
}

.document-info {
  flex: 1;
}

.document-title {
  font-weight: 600;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 4px;
}

.document-type {
  font-size: 11px;
  color: #94a3b8;
  text-transform: capitalize;
}

.view-doc-btn {
  padding: 6px 12px;
  font-size: 12px;
}

/* Order History Styles */
.envio-history-container {
  width: 100%;
  max-height: 75vh;
  overflow-y: auto;
  padding: 16px;
}

.order-history-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.order-summary-header-enhanced {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background: linear-gradient(135deg, #43454d 0%, #764ba2 100%);
  border-radius: 16px;
  color: white;
  flex-wrap: wrap;
  gap: 12px;
}

.order-badge-enhanced {
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.order-status-enhanced {
  padding: 8px 20px;
  border-radius: 30px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
}

.status-finished { background: #10b981; color: white; }
.status-started { background: #f59e0b; color: white; }
.status-transit { background: #3b82f6; color: white; }
.status-scheduled { background: #8b5cf6; color: white; }
.status-unknown { background: #6b7280; color: white; }

.quick-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
}

.stat-card-enhanced {
  background: white;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.stat-icon {
  font-size: 32px;
}

.stat-details {
  display: flex;
  flex-direction: column;
}

.stat-label-enhanced {
  font-size: 11px;
  text-transform: uppercase;
  color: #64748b;
}

.stat-value-enhanced {
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
}

.envio-sections {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.envio-section {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f8fafc;
  cursor: pointer;
}

.section-header h4 {
  flex: 1;
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
}

.section-toggle {
  font-size: 14px;
  color: #94a3b8;
}

.section-content {
  padding: 16px 20px;
  border-top: 1px solid #e2e8f0;
}

.customer-info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.info-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #f1f5f9;
  border-radius: 30px;
  font-size: 13px;
}

.items-grid-enhanced {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.item-card-enhanced {
  background: #f8fafc;
  border-radius: 10px;
  padding: 12px;
}

.item-name-enhanced strong {
  font-size: 14px;
  color: #1e293b;
}

.item-details {
  padding-top: 8px;
  border-top: 1px solid #e2e8f0;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  margin-bottom: 4px;
}

.detail-label {
  color: #64748b;
}

.timeline-enhanced {
  position: relative;
  padding-left: 30px;
}

.timeline-enhanced::before {
  content: '';
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to bottom, #3b82f6, #10b981, #f59e0b);
}

.timeline-item-enhanced {
  position: relative;
  margin-bottom: 24px;
}

.timeline-marker {
  position: absolute;
  left: -30px;
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 2px solid #e2e8f0;
  z-index: 1;
}

.timeline-marker.timeline-default-marker { background: #8b5cf6; color: white; border-color: #8b5cf6; }

.timeline-card {
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 16px;
}

.timeline-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.timeline-date-enhanced {
  font-size: 11px;
  color: #64748b;
}

.timeline-description-enhanced {
  font-size: 13px;
  color: #1e293b;
}

.history-loading {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
  margin: 0 auto 16px;
}

.no-history {
  text-align: center;
  padding: 60px 20px;
}

.no-history i {
  font-size: 48px;
  color: #cbd5e1;
  margin-bottom: 16px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 1100px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease;
  margin: auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
}

@keyframes modalSlideIn {
  from { transform: translateY(-30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px 24px 0 0;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-body::-webkit-scrollbar {
    display: none;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0 0 24px 24px;
}

.btn-secondary {
  background: rgba(241, 245, 249, 0.9);
  color: #475569;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  cursor: pointer;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/EvidenceModal.vue